import React, { createRef, useState, useEffect } from "react";
import { Select, Input } from "antd";
import { DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import {
  delete_attachment,
  upload_Attachments,
  get_attachments,
} from "../../../../Controllers/Accounting/Services/Attachments";
import URL from "../../../../Main/url";
import {   
  DocumentTextIcon,
  PDFIcon,
  ImageIcon,
} from "../../../../Componets/DocumentsIcons";
import { select_search } from "../../../../Controllers/Global";

const JournalEntriesTable = React.forwardRef((props, focus) => {
  const { entries, setEntries, documents, setDocuments, contra } = props;

  const [localContra, setLocalContra] = useState(contra);
  const [uploading, setUploading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowedAccounts, setAllowedAccounts] = useState([]);
  const predefinedCategories = [
    "Assets",
    "Liabilities",
    "Equity",
    "Expenses",
    "Income",
  ];

  useEffect(() => {
    setLocalContra(contra);
  }, [contra]);

  useEffect(() => {
    handleAccountSearch("", 0);
  }, []);



  const handleAccountSearch = async (searchText, index) => {
    setLoading(true);
    try {
      const select = {
        api: "get-all-accounts",
        search: searchText,
        status: 1,
        source: "ManualJournalEntry",
        isContra: contra,
      };

      const searchResult = await select_search(select);

      if (searchResult?.success) {
        const groupedOptions = predefinedCategories.map((category) => {
          const accountsInCategory = searchResult.data.data
            .filter((acc) => acc.category === category)
            .map((acc) => ({
              value: acc._id,
              label: `${acc.name} (${acc.code})`,
              type: acc.type,
              code: acc.code,
              contraAssociatedAccounts: acc.contraAssociatedAccounts || [],
            }));

          return {
            label: category,
            options: accountsInCategory,
          };
        });

        setAccounts(groupedOptions);
      } else {
        toast.error(searchResult?.message || "Failed to fetch accounts");
        setAccounts([]);
      }
    } catch (error) {
      console.error("Account search error:", error);
      toast.error("Error fetching accounts");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (value, index) => {
    let data = [...entries];
    data[index].account = value;

    const selectedAccount = accounts.find((acc) => acc.value === value);

    if (selectedAccount && localContra) {
      const associatedAccounts = selectedAccount.contraAssociatedAccounts || [];
      const allowed = associatedAccounts.map((acc) => ({
        value: acc._id,
        label: acc.name,
        type: acc.type,
        code: acc.code,
      }));

      setAllowedAccounts((prev) => {
        const updated = [...prev];
        updated[index + 1] = allowed;
        return updated;
      });

      if (data[index + 1]) data[index + 1].account = "";
    }

    setEntries(data);
  };

  const handleEntryChange = (value, index, field) => {
    let data = [...entries];
    if (field === "amount") {
      data[index][field] = Number(value) || 0;
    } else {
      data[index][field] = value;
    }
    setEntries(data);
  };

  const handleAddEntry = () => {
    setEntries((prev) => [
      ...prev,
      { id: "", account: "", type: "debit", amount: 0, reference: "" },
    ]);
  };

  const handleRemoveEntry = (index) => {
    if (entries.length > 2) {
      let data = [...entries];
      data.splice(index, 1);
      setEntries(data);
    } else {
      toast.error("At least two entries are required");
    }
  };

  const totalDebits = entries
    .filter((entry) => entry.type === "debit")
    .reduce((sum, entry) => sum + (entry.amount || 0), 0);

  const totalCredits = entries
    .filter((entry) => entry.type === "credit")
    .reduce((sum, entry) => sum + (entry.amount || 0), 0);

  const filterOption = (input, option) => {
    const labelMatch = (option.label ?? "")
      .toLowerCase()
      .includes(input.toLowerCase());
    const codeMatch = (option.code ?? "")
      .toLowerCase()
      .includes(input.toLowerCase());
    return labelMatch || codeMatch;
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    const validFiles = Array.from(files).filter((file) => {
      const isValidType =
        file.type.startsWith("image/") || file.type === "application/pdf";
      const isValidSize = file.size <= 10 * 1024 * 1024;

      if (!isValidType) {
        toast.error(
          `Invalid file type: ${file.name}. Only images and PDFs are allowed.`
        );
        return false;
      }
      if (!isValidSize) {
        toast.error(`File too large: ${file.name}. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setUploading(true);
    try {
      const formData = new FormData();
      validFiles.forEach((file) => formData.append("documents", file));

      const response = await upload_Attachments(formData);

      if (response.success) {
        const newDocuments = response.data.files.map((file) => ({
          ...file,
          id: Date.now() + Math.random(),
        }));
        setDocuments((prev) => [...prev, ...newDocuments]);
        toast.success(`${validFiles.length} document(s) uploaded successfully`);
        e.target.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("File upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = async (index) => {
    const documentToRemove = documents[index];
    try {
      if (documentToRemove.filename) {
        await delete_attachment(documentToRemove.filename);
      }
      setDocuments((prev) => prev.filter((_, i) => i !== index));
      toast.success("Document removed successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to remove document");
      setDocuments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const getFileIcon = (file) => {
    if (file.type === "pdf" || file.mimetype === "application/pdf") {
      return <PDFIcon className="w-6 h-6" />;
    } else if (file.type === "image" || file.mimetype?.startsWith("image/")) {
      return <ImageIcon className="w-6 h-6" />;
    } else {
      return <DocumentTextIcon className="w-6 h-6" />;
    }
  };

  const getFilePreviewUrl = (file) => {
    if (file.url) return `${URL}${file.url}`;
    if (file.filename) return get_attachments(file.filename);
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="pb-2 text-medium text-gray-500 text-sm">
          Supporting Documents (Images & PDF only, max 10MB each)
        </label>
        <div className="flex items-center gap-4 mb-4 mt-4">
          <input
            type="file"
            onChange={handleDocumentUpload}
            className="hidden"
            id="document-upload"
            multiple
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,image/*,application/pdf"
            disabled={uploading}
          />
          <label
            htmlFor="document-upload"
            className={`bg-blue-600 text-white px-6 py-2 rounded-lg cursor-pointer hover:bg-blue-700 flex items-center gap-2 ${
              uploading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <PaperClipOutlined />
            {uploading ? "Uploading..." : "Attach Files"}
          </label>
        </div>

        {documents.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-700">Attached Documents:</h4>
            {documents.map((doc, index) => (
              <div
                key={doc.id || index}
                className="flex items-center justify-between bg-white p-3 rounded-lg border"
              >
                <div className="flex items-center space-x-3">
                  {getFileIcon(doc)}
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate max-w-xs">
                      {doc.originalName || doc.name || `Document ${index + 1}`}
                    </span>
                    <span className="text-xs text-gray-500">
                      {doc.type?.toUpperCase()} • {(doc.size / 1024).toFixed(1)}{" "}
                      KB
                    </span>
                  </div>
                  {getFilePreviewUrl(doc) && (
                    <a
                      href={getFilePreviewUrl(doc)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm ml-2"
                    >
                      Preview
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeDocument(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  disabled={uploading}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <td className="border p-4 text-black">Account</td>
              <td className="border p-4 text-black">Type</td>
              <td className="border p-4 text-black">Amount</td>
              <td className="border p-4 text-black">Reference</td>
              <td className="border p-4 text-black">Remove</td>
            </tr>
          </thead>
          <tbody>
            {entries?.map((value, index) => {
              focus.current[`account${index}`] = createRef();
              focus.current[`amount${index}`] = createRef();

              return (
                <tr key={index}>
                  <td className="border p-4">
                    <Select
                      ref={focus.current[`account${index}`]}
                      className="w-full"
                      placeholder="Select Account"
                      allowClear={true}
                      showSearch={true}
                      filterOption={filterOption}
                      options={
                        localContra && allowedAccounts[index]
                          ? allowedAccounts[index]
                          : accounts
                      }
                      value={value.account || undefined}
                      onSearch={(e) => handleAccountSearch(e, index)}
                      onFocus={() => handleAccountSearch("", index)}
                      onChange={(e) => handleAccountChange(e, index)}
                      loading={loading}
                      notFoundContent={
                        loading ? "Loading..." : "No accounts found"
                      }
                    />
                  </td>
                  <td className="border p-4">
                    <Select
                      className="w-full"
                      value={value.type}
                      onChange={(e) => handleEntryChange(e, index, "type")}
                    >
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </Select>
                  </td>
                  <td className="border p-4">
                    <Input
                      ref={focus.current[`amount${index}`]}
                      type="number"
                      className="w-full"
                      placeholder="Amount"
                      value={value.amount}
                      onChange={(e) =>
                        handleEntryChange(e.target.value, index, "amount")
                      }
                      autoComplete="off"
                    />
                  </td>
                  <td className="border p-4">
                    <Input
                      type="text"
                      className="w-full"
                      placeholder="Reference"
                      value={value.reference}
                      onChange={(e) =>
                        handleEntryChange(e.target.value, index, "reference")
                      }
                      autoComplete="off"
                    />
                  </td>
                  <td className="border p-4 text-center">
                    <button
                      type="button"
                      className="text-lg text-red-500 cursor-pointer hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleRemoveEntry(index)}
                      disabled={entries.length <= 2}
                    >
                      <DeleteOutlined />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex justify-between mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-medium text-sm">
            Total Debit: {totalDebits.toFixed(2)}
          </div>
          <div className="text-medium text-sm">
            Total Credit: {totalCredits.toFixed(2)}
          </div>
          <div
            className={`text-medium text-sm ${
              Math.abs(totalDebits - totalCredits) > 0.001
                ? "text-red-600"
                : "text-green-600"
            }`}
          >
            Difference: {Math.abs(totalDebits - totalCredits).toFixed(2)}
          </div>
        </div>

        <div className="flex justify-end p-3">
          <button
            type="button"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            onClick={handleAddEntry}
          >
            Add Entry
          </button>
        </div>
      </div>
    </div>
  );
});

export default JournalEntriesTable;
